import type { ReactNode } from "react";

/** Why a magic field is not editable, in the order its reasons win. */
export type MagicFieldDisabledCause =
  | "prop"
  | "fieldset"
  | "form"
  | "deleted"
  | "permission";

export interface MagicFieldDisabledCauseState {
  cause: MagicFieldDisabledCause;
  /** Whether this cause disables the field now. */
  applies: boolean;
  /**
   * The text to show for it. `true` stands for the framework's text, but only
   * for the opt-in causes `deleted` and `permission`.
   */
  reason: ReactNode | true;
}

/**
 * Whether a reason has something to show: not `undefined`, `null`, a boolean
 * or an empty string.
 */
export function hasDisabledReason(reason: ReactNode | true): boolean {
  return (
    reason !== undefined &&
    reason !== null &&
    typeof reason !== "boolean" &&
    reason !== ""
  );
}

/**
 * The reason a disabled magic field shows: the first cause that applies and
 * has a reason, in the order given. A cause without a reason does not hide a
 * later one, so every reason shown is true. Returns `undefined` when no cause
 * that applies has a reason.
 */
export function resolveMagicFieldDisabledReason(
  causes: readonly MagicFieldDisabledCauseState[],
  frameworkText: (cause: "deleted" | "permission") => string,
): ReactNode {
  for (const { cause, applies, reason } of causes) {
    if (!applies) continue;
    if (reason === true && (cause === "deleted" || cause === "permission")) {
      return frameworkText(cause);
    }
    if (hasDisabledReason(reason)) return reason;
  }
  return undefined;
}
